import { gsap } from "gsap";
import { colors } from "../../../../theme";
// =========================================================
// DESKTOP NAVBAR ANIMATIONS
// =========================================================

// Initializes Navbar shrink/grow effect on scroll
export const initNavbarScrollAnimation = (navbarRef, logoImageRef, logoTextRef) => {
  let isScrolled = false;

  const handleScroll = () => {
    if (!navbarRef.current) return;
    
    if (window.scrollY > 50) {
      if (!isScrolled) {
        isScrolled = true;
        gsap.to(navbarRef.current, { height: "60px", marginTop: "0px", backgroundColor: colors.secondary.main, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", duration: 0.3, ease: "power2.out", overwrite: true });
        if (logoImageRef.current) gsap.to(logoImageRef.current, { width: 40, height: 40, duration: 0.3, overwrite: true });
        if (logoTextRef.current) gsap.to(logoTextRef.current, { fontSize: "16px", duration: 0.3, overwrite: true });
      }
    } else {
      if (isScrolled) {
        isScrolled = false;
        gsap.to(navbarRef.current, { height: "80px", marginTop: "0px", backgroundColor: colors.secondary.main, boxShadow: "none", duration: 0.3, ease: "power2.out", overwrite: true });
        if (logoImageRef.current) gsap.to(logoImageRef.current, { width: 50, height: 50, duration: 0.3, overwrite: true });
        if (logoTextRef.current) gsap.to(logoTextRef.current, { fontSize: "20px", duration: 0.3, overwrite: true });
      }
    }
  };
  
  // Set initial state
  if (navbarRef.current) {
    gsap.set(navbarRef.current, { height: "80px", backgroundColor: "#0f172a", marginTop: "0px" });
  }
  
  window.addEventListener("scroll", handleScroll);
  return () => window.removeEventListener("scroll", handleScroll);
};

// Stretchy underline animation on nav hover
export const animateNavHover = (underlineRef, target) => {
  if (!underlineRef.current || !target) return;
  
  const newLeft = target.offsetLeft;
  const newWidth = target.offsetWidth;
  const oldLeft = gsap.getProperty(underlineRef.current, "left") || newLeft;
  const oldWidth = gsap.getProperty(underlineRef.current, "width") || 0;

  gsap.killTweensOf(underlineRef.current);

  // Initial state: instant appearance
  if (oldWidth === 0) {
    gsap.set(underlineRef.current, { left: newLeft, width: newWidth });
    gsap.to(underlineRef.current, { opacity: 1, duration: 0.3 });
    return;
  }

  const isMovingRight = newLeft > oldLeft;
  const distance = Math.abs(newLeft - oldLeft);
  
  // Elastic stretch effect based on distance traveled
  const addedWidth = Math.min(distance * 0.15, 25);
  const stretchWidth = newWidth + addedWidth;
  const tl = gsap.timeline();

  if (isMovingRight) {
    tl.to(underlineRef.current, { width: stretchWidth, duration: 0.15, ease: "power2.out" })
      .to(underlineRef.current, { left: newLeft, width: newWidth, duration: 0.25, ease: "power3.out" }, "-=0.05");
  } else {
    tl.to(underlineRef.current, { left: oldLeft - addedWidth, width: oldWidth + addedWidth, duration: 0.15, ease: "power2.out" })
      .to(underlineRef.current, { left: newLeft, width: newWidth, duration: 0.25, ease: "power3.out" }, "-=0.05");
  }
};

// Fade and shrink underline on mouse leave
export const animateNavLeave = (underlineRef) => {
  if (!underlineRef.current) return;
  
  gsap.killTweensOf(underlineRef.current);
  gsap.to(underlineRef.current, { opacity: 0, width: 0, duration: 0.3 });
};

// =========================================================
// MOBILE NAVBAR ANIMATIONS
// =========================================================

export const animateMobileNavBgIn = (e) => {
  if (window.innerWidth >= 1051) return;
  const bg = e.currentTarget.querySelector('.mobile-hover-bg');
  const text = e.currentTarget.querySelector('.nav-link-content');
  const subItems = e.currentTarget.querySelectorAll('.mobile-lottery-item');

  if (bg) gsap.to(bg, { width: '100%', opacity: 1, duration: 1.2, ease: 'power3.out', overwrite: 'auto' });
  if (text) gsap.to(text, { color: colors.primary.contrastText, letterSpacing: '0.15em', duration: 1.2, ease: 'power3.out', overwrite: 'auto' });
  if (subItems.length > 0) gsap.to(subItems, { color: colors.primary.contrastText, duration: 1.2, ease: 'power3.out', overwrite: 'auto' });
};

export const animateMobileNavBgOut = (e) => {
  if (window.innerWidth >= 1051) return;
  const bg = e.currentTarget.querySelector('.mobile-hover-bg');
  const text = e.currentTarget.querySelector('.nav-link-content');
  const subItems = e.currentTarget.querySelectorAll('.mobile-lottery-item');

  if (bg) gsap.to(bg, { width: '0%', opacity: 0, duration: 1.25, ease: 'elastic.out', overwrite: 'auto' });
  if (text) gsap.to(text, { color: colors.primary.contrastText, letterSpacing: '0.05em', duration: 1.25, ease: 'elastic.out', overwrite: 'auto' });
  if (subItems.length > 0) gsap.to(subItems, { color: 'rgba(255, 255, 255, 0.75)', duration: 1.25, ease: 'elastic.out', overwrite: 'auto' });
};

export const initMobileMenuAnimation = (navbarRef, mobileMenuTl) => {
  if (window.innerWidth >= 1051) {
    // Desktop: clear mobile GSAP styles
    gsap.set(".navbarcollapse", { clearProps: "opacity,visibility" });
    gsap.set(".mobile-nav-item", { clearProps: "opacity,transform" });
    if (mobileMenuTl.current) mobileMenuTl.current.kill();
    mobileMenuTl.current = null;
    return null;
  }

  // Mobile: create the menu timeline
  const ctx = gsap.context(() => {
    gsap.set(".navbarcollapse", { autoAlpha: 0 });
    gsap.set(".mobile-nav-item", { opacity: 0, y: -18 });
    
    mobileMenuTl.current = gsap.timeline({ paused: true });
    mobileMenuTl.current
      .to(".navbarcollapse", { autoAlpha: 1, duration: 0.18, ease: "power2.out" })
      .to(".mobile-nav-item", { opacity: 1, y: 0, duration: 0.32, stagger: 0.07, ease: "power2.out" }, "-=0.06");
  }, navbarRef);

  return ctx;
};